// "Call now" (POST /patient/make-apcm-call, /patient/multiple/apcm-calls) and
// POST /daily/outbound_call. Every dial goes through graham's outreach policy:
// a refusal answers HTTP 409 with the reason in `detail` (outside weekdays
// 8am-7pm Central, patient refused, consent already on record, paused, ...).
// These endpoints need APCM ops access to the organization (403 otherwise).

export interface APCMCallResponse {
  call_id: string
  /** Always null now (Retell); kept only because the server still sends it. */
  room_url: null
  voice_provider: string
}

export interface RefusedCall {
  patient_id: string
  reason: string
}

export interface MultipleAPCMCallsResponse {
  started: number
  call_ids: string[]
  /** Patients the call policy refused, with the reason. */
  refused: RefusedCall[]
  /** Patients not found (active) in this organization. */
  failed: string[]
  missing_phone_numbers: string[]
}

export interface OutboundCallResponse {
  success: boolean
  voice_provider: string
  dialed: boolean
  closed_scheduled_rows: number
}

/** Why a dial request failed (shown under a "Call not placed" title). 409 = the server's policy reason. */
export function callErrorMessage(err: any): string {
  const status = err?.response?.status
  const detail = err?.response?.data?.detail
  const text = typeof detail === "string" && detail.trim() ? detail.trim() : null
  if (status === 409) return text || "Refused by the calling policy."
  if (status === 403) return "You need APCM ops access to this organization to place calls."
  if (text) return text
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? String(d)).join("; ")
  return err?.message || "Failed to place the call. Please try again."
}

interface NamedPatient {
  id: string
  first_name?: string | null
  last_name?: string | null
}

export interface CallProblem {
  patientId: string
  name: string
  reason: string
}

export interface MultipleCallsSummary {
  started: number
  requested: number
  problems: CallProblem[]
  /** One line for a toast / heading. */
  headline: string
}

/** Every patient that did not get a call, named, with why. */
export function summarizeMultipleCalls(
  result: MultipleAPCMCallsResponse,
  patients: NamedPatient[],
): MultipleCallsSummary {
  const byId = new Map(patients.map((p) => [String(p.id), p]))
  const nameOf = (id: string) => {
    const p = byId.get(String(id))
    const name = p ? `${p.first_name || ""} ${p.last_name || ""}`.trim() : ""
    return name || String(id).slice(0, 8)
  }
  const problems: CallProblem[] = [
    ...(result.refused || []).map((r) => ({ patientId: r.patient_id, name: nameOf(r.patient_id), reason: r.reason })),
    ...(result.missing_phone_numbers || []).map((id) => ({ patientId: id, name: nameOf(id), reason: "no phone number on file" })),
    ...(result.failed || []).map((id) => ({ patientId: id, name: nameOf(id), reason: "not an active patient of this organization" })),
  ]
  const started = result.started ?? result.call_ids?.length ?? 0
  const requested = patients.length
  const headline =
    problems.length === 0
      ? `Calls placed to all ${started} patient${started === 1 ? "" : "s"}.`
      : `${started} of ${requested} call${requested === 1 ? "" : "s"} placed; ${problems.length} not placed.`
  return { started, requested, problems, headline }
}
